/* Contains functions that construct GraphQL queries to be used with Shopify's
Admin API client. Pure helpers live in ../utils/admin.helpers.ts. */

import {escapeSearchQueryValue, makeShopifyRequest} from "../request";
import type {
	CreateRefundMutation,
	GetOrderQuery,
	CreateProductMutation,
	CreateProductMutationVariables,
	FindProductByHandleQuery,
	FindProductByHandleQueryVariables,
	GetProductsByCourseOfferingIdQuery,
	GetProductsByCourseOfferingIdQueryVariables,
	UpdateProductMutation,
	UpdateProductMutationVariables,
	CreateProductVariantMutation,
	CreateProductVariantMutationVariables,
	UpdateProductVariantMutation,
	UpdateProductVariantMutationVariables,
	DeleteProductVariantMutation,
	DeleteProductVariantMutationVariables,
	FindVariantBySkuQuery,
	FindVariantBySkuQueryVariables,
	GetVariantMetafieldQuery,
	GetVariantMetafieldQueryVariables,
	DoesCustomerExistInShopifyQuery,
	DoesCustomerExistInShopifyQueryVariables,
	UpsertCustomerMutation,
	UpsertCustomerMutationVariables,
	CustomerCreateMutation,
	CustomerDeleteMutation,
	CustomerUpdateMutation,
	GetCustomerByEmailQuery,
	MetafieldsDeleteMutation,
	MetafieldsSetMutation,
	MetafieldsSetMutationVariables,
	GetCustomerByEmailWithCartMetafieldQuery,
	CreateFulfillmentMutation,
	GetOrderNoteQuery,
	OrderFulfillmentOrdersQuery,
	FindInventoryItemBySkuQuery,
	FindInventoryItemBySkuQueryVariables,
	OrderLineItemsForEnrollmentQuery,
	AdjustInventoryBySkuMutation,
	AdjustInventoryBySkuMutationVariables,
	UpdateOrderNoteMutation,
	UpdateOrderNoteMutationVariables,
	TagsAddMutation,
	TagsAddMutationVariables,
	UpsertCampusMetaobjectMutationVariables,
	UpsertCampusMetaobjectMutation,
	UpsertCourseOfferingMetaobjectMutation,
	UpsertCourseOfferingMetaobjectMutationVariables,
	UpsertMeetingTimeMetaobjectMutation,
	UpsertMeetingTimeMetaobjectMutationVariables,
	UpsertClassScheduleMetaobjectMutation,
	UpsertClassScheduleMetaobjectMutationVariables,
	UpsertCampusBuildingMetaobjectMutation,
	UpsertCampusBuildingMetaobjectMutationVariables,
	UpsertClassMetaobjectMutation,
	UpsertClassMetaobjectMutationVariables,
} from "../codegen/admin.generated";
import type {
	MetafieldInput,
	ProductCreateInput,
	ProductUpdateInput,
	ProductVariantsBulkInput,
	CustomerSetInput,
	MetaobjectStatus,
} from "../codegen/admin.types";
import {calculateRefundAmount} from "../refunds";
import {log} from "../log";
import {
	METAFIELD_NAMESPACE,
	findEnrollmentLineItemId,
	formatUserErrors,
	selectFulfillableLineItems,
	toShopifyCustomer,
	toShopifyGid,
	withNamespace,
	type ToShopifyCustomerInput,
} from "../utils/admin.helpers";
import {findProductIdByCourseOfferingIdAndCampusId} from "./admin/findProductIdByCourseOfferingIdAndCampusId";
import {
	getCourseOfferingWithSchedule,
	type CourseOfferingProduct,
} from "./admin/getCourseOfferingWithSchedule";
export type {
	CourseOfferingClass,
	CourseOfferingClassInstances,
	CourseOfferingClassSchedule,
	CourseOfferingMeetingTime,
	CourseOfferingProduct,
} from "./admin/getCourseOfferingWithSchedule";
export type {
	CustomerSetInput,
	MetafieldInput,
	ProductStatus,
} from "../codegen/admin.types";

type CustomerUpdateInput = {
	firstName?: string;
	lastName?: string;
	platformUserId: string;
};

export interface FulfillLineItemInput {
	orderId: string;
	lineItemId: string;
}

export interface FindOrderLineItemForEnrollmentInput {
	orderId: string;
	classId: string;
	studentPlatformUserId: string;
}

// Our Shopify store's location ID
export const DEFAULT_LOCATION_ID = "gid://shopify/Location/109001998621";

async function getOrderNote(orderId: string): Promise<string> {
	const orderGid = toShopifyGid("Order", orderId);
	const response = await makeShopifyRequest<GetOrderNoteQuery>(
		"admin",
		`#graphql
			query GetOrderNote($orderId: ID!) {
				order(id: $orderId) {
					id
					note
				}
			}
		`,
		{orderId: orderGid}
	);
	if (!response.order) {
		throw new Error(
			`Shopify order note fetch failed: order "${orderId}" not found`
		);
	}
	return response.order.note ?? "";
}

// shopify docs state that orderUpdate will overwrite the existing note
async function replaceOrderNote(orderId: string, note: string): Promise<void> {
	const orderGid = toShopifyGid("Order", orderId);
	const variables: UpdateOrderNoteMutationVariables = {
		input: {id: orderGid, note},
	};
	const response = await makeShopifyRequest<UpdateOrderNoteMutation>(
		"admin",
		`#graphql
			mutation UpdateOrderNote($input: OrderInput!) {
				orderUpdate(input: $input) {
					order {
						id
						note
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	if (!response.orderUpdate) {
		throw new Error(
			"Shopify order note update failed: orderUpdate missing from Shopify response"
		);
	}

	const {userErrors} = response.orderUpdate;
	if (userErrors.length) {
		throw new Error(
			`Shopify order note update failed: ${formatUserErrors(userErrors)}`
		);
	}
}

/**
 * Appends text to an order's existing note, separated by a newline. If the
 * order has no note yet, sets it to text. Throws if the order is not found
 * or Shopify returns mutation-level userErrors.
 */
export async function appendOrderNote(
	orderId: string,
	text: string
): Promise<void> {
	const existing = await getOrderNote(orderId);
	const updated = existing ? `${existing}\n${text}` : text;
	await replaceOrderNote(orderId, updated);
}

/**
 * Adds a tag to an order, leaving any existing tags in place. Throws if the
 * order is not found or Shopify returns mutation-level userErrors.
 */
export async function addOrderTag(orderId: string, tag: string): Promise<void> {
	const orderGid = toShopifyGid("Order", orderId);
	const variables: TagsAddMutationVariables = {id: orderGid, tags: [tag]};
	const response = await makeShopifyRequest<TagsAddMutation>(
		"admin",
		`#graphql
			mutation TagsAdd($id: ID!, $tags: [String!]!) {
				tagsAdd(id: $id, tags: $tags) {
					node {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	if (!response.tagsAdd) {
		throw new Error(
			"Shopify order tag add failed: tagsAdd missing from Shopify response"
		);
	}

	const {userErrors} = response.tagsAdd;
	if (userErrors.length) {
		throw new Error(
			`Shopify order tag add failed: ${formatUserErrors(userErrors)}`
		);
	}
}
const GET_ORDER_QUERY = `#graphql
	query GetOrder($orderId: ID!) {
		order(id: $orderId) {
			id
			transactions(first: 10) {
				id
				gateway
				kind
				amount
				status
			}
			lineItems(first: 50) {
				edges {
					node {
						id
						title
						quantity
						refundableQuantity
						priceAfterAllDiscountsBeforeTaxesSet {
							shopMoney { amount }
						}
						taxLines {
							priceSet {
								shopMoney { amount }
							}
						}
					}
				}
			}
		}
	}`;

const CUSTOMER_FRAGMENT = `#graphql
	fragment CustomerFragment on Customer {
		email
		firstName
		id
		lastName
		metafield(namespace: "custom", key: "platform_user_id") {
			id
			value
		}
	}`;

export async function getOrderItems(orderId: string) {
	const {order} = await makeShopifyRequest<GetOrderQuery>(
		"admin",
		GET_ORDER_QUERY,
		{orderId}
	);
	if (!order) throw new Error("No such order");

	return order.lineItems.edges.map(({node: item}) => {
		const {id, quantity, refundableQuantity, title} = item;
		const refundableAmount = calculateRefundAmount(item);
		return {id, title, quantity, refundableQuantity, refundableAmount};
	});
}

export async function refundEnrollment(
	orderId: string,
	lineItemId: string,
	amount: string,
	reason?: string
) {
	const {order} = await makeShopifyRequest<GetOrderQuery>(
		"admin",
		GET_ORDER_QUERY,
		{orderId}
	);
	if (!order) throw new Error("No such order");

	const saleTransaction = order.transactions.find(
		({kind, status}) =>
			status === "SUCCESS" && (kind === "SALE" || kind === "CAPTURE")
	);
	if (!saleTransaction) throw new Error("No sale to refund");

	const targetItem = order.lineItems.edges.find(
		({node: item}) => item.id === lineItemId
	);
	if (!targetItem) throw new Error("No such item in order");

	const quantity = targetItem.node.refundableQuantity;
	if (quantity === 0) throw new Error("Item already refunded");

	const {refundCreate} = await makeShopifyRequest<CreateRefundMutation>(
		"admin",
		`#graphql
			mutation CreateRefund($input: RefundInput!, $idempotencyKey: String!) {
				refundCreate(input: $input) @idempotent(key: $idempotencyKey) {
					refund {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		{
			idempotencyKey: crypto.randomUUID(),
			input: {
				orderId,
				note: reason,
				refundLineItems: [
					{
						lineItemId,
						quantity,
						/**
						 * Defer restocking until registrar emits `registrar.enrollment.dropped`
						 * because refund does not always include a drop nor vice versa.
						 */
						restockType: "NO_RESTOCK",
					},
				],
				transactions: [
					{
						orderId,
						kind: "REFUND",
						gateway: saleTransaction.gateway,
						amount,
						parentId: saleTransaction.id,
					},
				],
			},
		}
	);
	if (!refundCreate) throw new Error("Unexpected refund error");

	const {refund, userErrors} = refundCreate;
	if (userErrors.length) {
		throw new Error(`Refund failed:\n${JSON.stringify(userErrors)}`);
	}
	if (!refund) throw new Error("Shopify returned no refund");

	return {refundId: refund.id};
}

export type ShopifyCustomer = {
	/** The Shopify ID representing the customer instance */
	customerShopifyId: string;
	email: string;
};

export type ShopifyCustomerWithCartMetafield = ShopifyCustomer & {
	/** The Shopify ID representing the cart instance i.e. the value of the cart metafield */
	cartId: string | null;
	/**
	 * The Shopify ID representing the customer metafield `custom.cart_id`
	 * itself, not an instance; it does **NOT** represent the stored value of the
	 * metafield i.e. **NOT** a Shopify cart ID. It represents the field itself so
	 * that our requests know on which field to insert the value for the customer's
	 * cart ID.
	 */
	cartMetafieldId: string | null;
};

/** A product read from Shopify (inferred from findProductByHandle). */
export type ShopifyProduct = NonNullable<
	Awaited<ReturnType<typeof findProductByHandle>>
>;

/**
 * Returns the Shopify product with the given handle, or null if none exists.
 * Throws if the Shopify request fails.
 */
export async function findProductByHandle(handle: string) {
	const variables: FindProductByHandleQueryVariables = {
		query: `handle:"${escapeSearchQueryValue(handle)}"`,
		namespace: METAFIELD_NAMESPACE,
	};
	const response = await makeShopifyRequest<FindProductByHandleQuery>(
		"admin",
		`#graphql
			query FindProductByHandle($query: String!, $namespace: String!) {
				products(first: 1, query: $query) {
					nodes {
						id
						title
						handle
						options {
							id
							name
						}
						metafields(namespace: $namespace, first: 50) {
							nodes {
								key
								value
							}
						}
					}
				}
			}
		`,
		variables
	);

	const node = response.products.nodes[0];
	if (!node) return null;
	return {
		...node,
		metafields: node.metafields.nodes,
	};
}

/**
 * Returns Shopify products linked to course_offering metaobjects whose
 * course_offering_id matches the given value. Multiple products may share the
 * same course_offering_id via different course_offering metaobjects (e.g.
 * different campus data).
 */
export async function getProductsByCourseOfferingId(courseOfferingId: string) {
	const variables: GetProductsByCourseOfferingIdQueryVariables = {
		type: "course_offering",
		query: `fields.course_offering_id:"${escapeSearchQueryValue(courseOfferingId)}"`,
	};
	const response = await makeShopifyRequest<GetProductsByCourseOfferingIdQuery>(
		"admin",
		`#graphql
			query GetProductsByCourseOfferingId($type: String!, $query: String!) {
				# TODO: What's the right limit here?
				metaobjects(first: 100, type: $type, query: $query) {
					nodes {
						id
						handle
						# TODO: What's the right limit here?
						referencedBy(first: 100) {
							nodes {
								key
								namespace
								product: referencer {
									__typename
									... on Product {
										id
										handle
										title
										metafield(namespace: "custom", key: "course_offering") {
											reference {
												__typename
												... on Metaobject {
													id
													handle
													courseOfferingIdField: field(key: "course_offering_id") {
														value
													}
													campusField: field(key: "campus") {
														reference {
															__typename
															... on Metaobject {
																id
																campusIdField: field(key: "campus_id") {
																	value
																}
																campusNameField: field(key: "name") {
																	value
																}
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		variables
	);

	return response.metaobjects.nodes.flatMap((courseOffering) =>
		courseOffering.referencedBy.nodes.flatMap((relation) => {
			const product = relation.product;

			// Make sure the referenced node is actually a product.
			if (product.__typename !== "Product") return [];

			// Make sure the product actually has a courseOffering metaobject reference.
			const courseOfferingRef = product.metafield?.reference;
			if (courseOfferingRef?.__typename !== "Metaobject") return [];

			// Shopify can return unrelated entries when the queried field is not
			// filterable. Never let another offering suppress product creation.
			if (courseOfferingRef.courseOfferingIdField?.value !== courseOfferingId)
				return [];

			// Grab the campus data, if any.
			let campus: {campusId: string; name: string} | null = null;
			const campusRef = courseOfferingRef.campusField?.reference;
			if (
				campusRef &&
				campusRef.__typename === "Metaobject" &&
				campusRef.campusIdField?.value &&
				campusRef.campusNameField?.value
			) {
				campus = {
					campusId: campusRef.campusIdField?.value,
					name: campusRef.campusNameField?.value,
				};
			}

			return [
				{
					id: product.id,
					handle: product.handle,
					title: product.title,
					courseOffering: {
						handle: courseOfferingRef.handle,
						courseOfferingId:
							courseOfferingRef.courseOfferingIdField?.value ?? null,
						campus,
					},
				},
			];
		})
	);
}

/**
 * Creates a Shopify product. This is idempotent by product handle, so
 * it skips creation if a product with the same handle already exists
 * and returns the existing product instead.
 *
 * When optionName is provided, the product is created with that option
 * (and a placeholder value) so future variants can use it without
 * adding variants now.
 */
export async function createProduct(params: {
	product: ProductCreateInput;
	optionName?: string;
}) {
	const {product, optionName} = params;
	const variables: CreateProductMutationVariables = {
		product: {
			...product,
			...(optionName && {
				productOptions: [{name: optionName, values: [{name: "Default Title"}]}],
			}),
		},
	};

	// TODO: Can/should this be replaced with an upsert instead?
	const response = await makeShopifyRequest<CreateProductMutation>(
		"admin",
		`#graphql
			mutation CreateProduct($product: ProductCreateInput!) {
				productCreate(product: $product) {
					product {
						id
						handle
						options {
							id
							name
						}
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on productCreate, and since we
	// don't want a response that doesn't have it to be treated like a success.
	if (!response.productCreate) {
		throw new Error(
			"Shopify product creation failed: productCreate missing from response"
		);
	}

	const {product: createdProduct, userErrors} = response.productCreate;
	if (userErrors.length) {
		throw new Error(
			`Shopify product creation failed: ${formatUserErrors(userErrors)}`
		);
	}

	if (!createdProduct) {
		throw new Error(
			"Shopify product creation failed: product missing from Shopify response"
		);
	}

	return createdProduct;
}

// TODO: A better way to type this?
export type CampusMetaobject = {
	campusId: string;
	name: string;
	timezone: string;
};

export async function upsertCampusMetaobject(
	campus: CampusMetaobject
): Promise<string> {
	const variables: UpsertCampusMetaobjectMutationVariables = {
		handle: {handle: campus.campusId, type: "campus"},
		metaobject: {
			capabilities: {
				publishable: {status: "ACTIVE" as MetaobjectStatus},
			},
			fields: [
				{key: "campus_id", value: campus.campusId},
				{key: "name", value: campus.name},
				{key: "timezone", value: campus.timezone},
			],
		},
	};
	const response = await makeShopifyRequest<UpsertCampusMetaobjectMutation>(
		"admin",
		`#graphql
			mutation UpsertCampusMetaobject(
				$handle: MetaobjectHandleInput!,
				$metaobject: MetaobjectUpsertInput!
			) {
				metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
					metaobject { id }
					userErrors { field message }
				}
			}
		`,
		variables
	);

	// TODO: Logging / error handling?
	if (!response.metaobjectUpsert) {
		throw new Error(
			"Shopify campus metaobject upsert failed: metaobjectUpsert missing from Shopify response"
		);
	}

	if (response.metaobjectUpsert.userErrors.length) {
		throw new Error(
			`Shopify campus metaobject upsert failed: ${formatUserErrors(response.metaobjectUpsert.userErrors)}`
		);
	}

	if (!response.metaobjectUpsert.metaobject) {
		throw new Error(
			"Shopify campus metaobject upsert failed: metaobject missing from Shopify response"
		);
	}

	return response.metaobjectUpsert.metaobject.id;
}

// TODO: A better way to type this?
export type CourseOfferingMetaobject = {
	id: string;
	courseId: string;
	track?: string;
	modality: string;
	subject: string;
	campus?: {
		id: string;
		name: string;
		timezone: string;
		campusMetaobjectId: string;
	};
};
export async function upsertCourseOfferingMetaobject(
	courseOffering: CourseOfferingMetaobject
): Promise<string> {
	// TODO: Should campus be created here or separately?
	const variables: UpsertCourseOfferingMetaobjectMutationVariables = {
		handle: {
			handle: courseOffering.campus
				? `${courseOffering.id}-${courseOffering.campus.id}`
				: courseOffering.id,
			type: "course_offering",
		},
		metaobject: {
			capabilities: {
				publishable: {status: "ACTIVE" as MetaobjectStatus},
			},
			fields: [
				{key: "course_offering_id", value: courseOffering.id},
				{key: "course_id", value: courseOffering.courseId},
				{key: "modality", value: courseOffering.modality},
				{key: "subject", value: courseOffering.subject},
				{key: "programs", value: JSON.stringify([])}, // TODO: Rework this -- comes from Sanity.
				{key: "program_groups", value: JSON.stringify([])}, // TODO: Same as programs; the definition requires it.
				...(courseOffering.track
					? [{key: "track", value: courseOffering.track}]
					: []),
				...(courseOffering.campus
					? [{key: "campus", value: courseOffering.campus.campusMetaobjectId}]
					: []),
			],
		},
	};

	const response =
		await makeShopifyRequest<UpsertCourseOfferingMetaobjectMutation>(
			"admin",
			`#graphql
			mutation UpsertCourseOfferingMetaobject(
				$handle: MetaobjectHandleInput!,
				$metaobject: MetaobjectUpsertInput!
			) {
				metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
					metaobject { id }
					userErrors { field message }
				}
			}
		`,
			variables
		);

	if (!response.metaobjectUpsert) {
		throw new Error(
			"Shopify course offering metaobject upsert failed: metaobjectUpsert missing from Shopify response"
		);
	}

	if (response.metaobjectUpsert.userErrors.length) {
		throw new Error(
			`Shopify course offering metaobject upsert failed: ${formatUserErrors(response.metaobjectUpsert.userErrors)}`
		);
	}

	if (!response.metaobjectUpsert.metaobject) {
		throw new Error(
			"Shopify course offering metaobject upsert failed: metaobject missing from Shopify response"
		);
	}

	return response.metaobjectUpsert.metaobject.id;
}

/**
 * Updates an existing Shopify product, identified by its handle. Only the
 * fields present in the input are sent to Shopify, so callers can update any
 * subset of fields. Throws if no handle is provided, or if no product exists
 * with the provided handle.
 */
export async function updateProduct(
	product: ProductUpdateInput
): Promise<void> {
	const {handle} = product;
	if (!handle) {
		throw new Error("Shopify product update failed: handle is required");
	}

	const existingProduct = await findProductByHandle(handle);
	if (!existingProduct) {
		throw new Error(
			`Shopify product update failed: no product found with handle "${handle}"`
		);
	}

	const variables: UpdateProductMutationVariables = {
		product: {
			...product,
			id: existingProduct.id,
			metafields: withNamespace(product.metafields),
		},
	};
	const response = await makeShopifyRequest<UpdateProductMutation>(
		"admin",
		`#graphql
			mutation UpdateProduct($product: ProductUpdateInput!) {
				productUpdate(product: $product) {
					product {
						id
						handle
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on productUpdate, and since we
	// don't want a response that doesn't have it to be treated like a success.
	if (!response.productUpdate) {
		throw new Error(
			"Shopify product update failed: productUpdate missing from Shopify response"
		);
	}

	const {userErrors} = response.productUpdate;
	if (userErrors.length) {
		throw new Error(
			`Shopify product update failed: ${formatUserErrors(userErrors)}`
		);
	}
}

/**
 * Returns the Shopify product variant ID for the variant with the given SKU,
 * or null if none exists. Throws if the Shopify request fails.
 */
export async function findProductVariantBySku(
	sku: string
): Promise<string | null> {
	const variables: FindVariantBySkuQueryVariables = {
		query: `sku:"${escapeSearchQueryValue(sku)}"`,
	};
	const response = await makeShopifyRequest<FindVariantBySkuQuery>(
		"admin",
		`#graphql
			query FindVariantBySku($query: String!) {
				productVariants(first: 1, query: $query) {
					nodes {
						id
					}
				}
			}
		`,
		variables
	);

	return response.productVariants.nodes[0]?.id ?? null;
}

/**
 * Creates a Shopify product variant under the product with the given id.
 * Idempotent by SKU: skips creation if a variant with the same SKU already
 * exists. Throws if no SKU is provided for the variant.
 *
 * Note that Shopify's singular productVariantCreate is deprecated, so we use
 * the "bulk" mutation here; we just pass a one-element array.
 */
export async function createProductVariant(
	productId: string,
	variant: ProductVariantsBulkInput
): Promise<void> {
	const sku = variant.inventoryItem?.sku;
	if (!sku) {
		throw new Error(
			"Shopify product variant creation failed: inventoryItem.sku is required"
		);
	}

	const existingVariantId = await findProductVariantBySku(sku);
	if (existingVariantId) return;

	const variables: CreateProductVariantMutationVariables = {
		productId,
		variants: [
			{
				...variant,
				metafields: withNamespace(variant.metafields ?? undefined),
			},
		],
	};
	const response = await makeShopifyRequest<CreateProductVariantMutation>(
		"admin",
		`#graphql
			mutation CreateProductVariant(
				$productId: ID!
				$variants: [ProductVariantsBulkInput!]!
			) {
				productVariantsBulkCreate(
					productId: $productId
					variants: $variants
					strategy: PRESERVE_STANDALONE_VARIANT
				) {
					productVariants {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on the result, and since we
	// don't want a response that doesn't have it to be treated like a success.
	if (!response.productVariantsBulkCreate) {
		throw new Error(
			"Shopify product variant creation failed: productVariantsBulkCreate missing from Shopify response"
		);
	}

	const {userErrors} = response.productVariantsBulkCreate;
	if (userErrors.length) {
		throw new Error(
			`Shopify product variant creation failed: ${formatUserErrors(userErrors)}`
		);
	}
}

/**
 * Updates the Shopify product variant identified by its SKU under the product
 * with the given id. Throws if the variant does not exist.
 *
 * Note that Shopify's singular productVariantUpdate is deprecated, so we use
 * the "bulk" mutation here; we just pass a one-element array.
 */
export async function updateProductVariant(
	productId: string,
	sku: string,
	variant: ProductVariantsBulkInput
): Promise<void> {
	const variantId = await findProductVariantBySku(sku);
	if (!variantId) {
		throw new Error(
			`Shopify product variant update failed: existing variant not found for SKU "${sku}"`
		);
	}

	const variables: UpdateProductVariantMutationVariables = {
		productId,
		variants: [
			{
				...variant,
				id: variantId,
				metafields: withNamespace(variant.metafields),
			},
		],
	};
	const response = await makeShopifyRequest<UpdateProductVariantMutation>(
		"admin",
		`#graphql
			mutation UpdateProductVariant(
				$productId: ID!
				$variants: [ProductVariantsBulkInput!]!
			) {
				productVariantsBulkUpdate(productId: $productId, variants: $variants) {
					productVariants {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on the result, and since we
	// don't want a response that doesn't have it to be treated like a success.
	if (!response.productVariantsBulkUpdate) {
		throw new Error(
			"Shopify product variant update failed: productVariantsBulkUpdate missing from Shopify response"
		);
	}

	const {userErrors} = response.productVariantsBulkUpdate;
	if (userErrors.length) {
		throw new Error(
			`Shopify product variant update failed: ${formatUserErrors(userErrors)}`
		);
	}
}

/**
 * Deletes the Shopify product variant identified by its SKU under the product
 * with the given id. No-ops if the variant doesn't exist, since there's
 * nothing left to delete.
 */
export async function deleteProductVariant(
	productId: string,
	sku: string
): Promise<void> {
	const variantId = await findProductVariantBySku(sku);
	if (!variantId) return;

	const variables: DeleteProductVariantMutationVariables = {
		productId,
		variantsIds: [variantId],
	};
	const response = await makeShopifyRequest<DeleteProductVariantMutation>(
		"admin",
		`#graphql
			mutation DeleteProductVariant(
				$productId: ID!
				$variantsIds: [ID!]!
			) {
				productVariantsBulkDelete(
					productId: $productId
					variantsIds: $variantsIds
				) {
					product {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on the result, and since we
	// don't want a response that doesn't have it to be treated like a success.
	if (!response.productVariantsBulkDelete) {
		throw new Error(
			"Shopify product variant deletion failed: productVariantsBulkDelete missing from Shopify response"
		);
	}

	const {userErrors} = response.productVariantsBulkDelete;
	if (userErrors.length) {
		throw new Error(
			`Shopify product variant deletion failed: ${formatUserErrors(userErrors)}`
		);
	}
}

export type MeetingTimeMetaobject = {
	day: string;
	timeStartAt: string;
	timeEndAt: string;
};

// meeting_times has no class-specific data (just day/start/end), so it's a
// shared value object — many classes with the same weekly slot reuse the same
// metaobject, the same way campus_building is shared by buildingId alone
// rather than scoped per class.
export async function upsertMeetingTimeMetaobject(
	meetingTime: MeetingTimeMetaobject
): Promise<string> {
	// Shopify handles don't allow colons, so "14:45" becomes "1445".
	const variables: UpsertMeetingTimeMetaobjectMutationVariables = {
		handle: {
			handle: `${meetingTime.day}-${meetingTime.timeStartAt.replace(":", "")}-${meetingTime.timeEndAt.replace(":", "")}`,
			type: "meeting_times",
		},
		metaobject: {
			capabilities: {publishable: {status: "ACTIVE" as MetaobjectStatus}},
			fields: [
				{key: "day", value: meetingTime.day},
				{key: "time_start_at", value: meetingTime.timeStartAt},
				{key: "time_end_at", value: meetingTime.timeEndAt},
			],
		},
	};
	const response =
		await makeShopifyRequest<UpsertMeetingTimeMetaobjectMutation>(
			"admin",
			`#graphql
			mutation UpsertMeetingTimeMetaobject(
				$handle: MetaobjectHandleInput!
				$metaobject: MetaobjectUpsertInput!
			) {
				metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
					metaobject {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
			variables
		);

	if (!response.metaobjectUpsert) {
		throw new Error(
			"Shopify meeting time metaobject upsert failed: metaobjectUpsert missing from Shopify response"
		);
	}

	const {userErrors, metaobject} = response.metaobjectUpsert;
	if (userErrors.length) {
		throw new Error(
			`Shopify meeting time metaobject upsert failed: ${formatUserErrors(userErrors)}`
		);
	}
	if (!metaobject) {
		throw new Error(
			"Shopify meeting time metaobject upsert failed: metaobject missing from Shopify response"
		);
	}

	return metaobject.id;
}

export type ClassScheduleMetaobject = {
	type: "standard" | "drop-in";
	dateStartAt: string;
	dateEndAt: string;
	timezone: string;
	meetingTimeMetaobjectIds: string[];
};

/**
 * Upserts a "class_schedule" metaobject by deterministic handle = classId,
 * meaning there is one schedule unique per class
 */
export async function upsertClassScheduleMetaobject(
	classId: string,
	schedule: ClassScheduleMetaobject
): Promise<string> {
	const variables: UpsertClassScheduleMetaobjectMutationVariables = {
		handle: {handle: classId, type: "class_schedule"},
		metaobject: {
			capabilities: {publishable: {status: "ACTIVE" as MetaobjectStatus}},
			fields: [
				{key: "type", value: schedule.type},
				{key: "date_start_at", value: schedule.dateStartAt},
				{key: "date_end_at", value: schedule.dateEndAt},
				{key: "timezone", value: schedule.timezone},
				...(schedule.meetingTimeMetaobjectIds.length
					? [
							{
								key: "meetings_times",
								value: JSON.stringify(schedule.meetingTimeMetaobjectIds),
							},
						]
					: []),
			],
		},
	};
	const response =
		await makeShopifyRequest<UpsertClassScheduleMetaobjectMutation>(
			"admin",
			`#graphql
			mutation UpsertClassScheduleMetaobject(
				$handle: MetaobjectHandleInput!
				$metaobject: MetaobjectUpsertInput!
			) {
				metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
					metaobject {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
			variables
		);

	if (!response.metaobjectUpsert) {
		throw new Error(
			"Shopify class schedule metaobject upsert failed: metaobjectUpsert missing from Shopify response"
		);
	}

	const {userErrors, metaobject} = response.metaobjectUpsert;
	if (userErrors.length) {
		throw new Error(
			`Shopify class schedule metaobject upsert failed: ${formatUserErrors(userErrors)}`
		);
	}
	if (!metaobject) {
		throw new Error(
			"Shopify class schedule metaobject upsert failed: metaobject missing from Shopify response"
		);
	}

	return metaobject.id;
}

export type CampusBuildingMetaobject = {
	buildingId: string;
	name: string;
	isPrimary: boolean;
};

export async function upsertCampusBuildingMetaobject(
	building: CampusBuildingMetaobject
): Promise<string> {
	const variables: UpsertCampusBuildingMetaobjectMutationVariables = {
		handle: {handle: building.buildingId, type: "campus_building"},
		metaobject: {
			capabilities: {publishable: {status: "ACTIVE" as MetaobjectStatus}},
			fields: [
				{key: "building_id", value: building.buildingId},
				{key: "name", value: building.name},
				{key: "is_primary", value: String(building.isPrimary)},
			],
		},
	};
	const response =
		await makeShopifyRequest<UpsertCampusBuildingMetaobjectMutation>(
			"admin",
			`#graphql
			mutation UpsertCampusBuildingMetaobject(
				$handle: MetaobjectHandleInput!
				$metaobject: MetaobjectUpsertInput!
			) {
				metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
					metaobject {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
			variables
		);

	if (!response.metaobjectUpsert) {
		throw new Error(
			"Shopify campus building metaobject upsert failed: metaobjectUpsert missing from Shopify response"
		);
	}

	const {userErrors, metaobject} = response.metaobjectUpsert;
	if (userErrors.length) {
		throw new Error(
			`Shopify campus building metaobject upsert failed: ${formatUserErrors(userErrors)}`
		);
	}
	if (!metaobject) {
		throw new Error(
			"Shopify campus building metaobject upsert failed: metaobject missing from Shopify response"
		);
	}

	return metaobject.id;
}

// Mirrors the choices list on the "class" metaobject's lifecycle_status field.
export const CLASS_LIFECYCLE_STATUS = {
	INCOMPLETE: "incomplete",
	SCHEDULED: "scheduled",
	PUBLISHED: "published",
	ON_SALE: "on-sale",
	REGISTRATION_CLOSED: "registration-closed",
	CANCELED: "canceled",
	READY_TO_ARCHIVE: "ready-to-archive",
	ARCHIVED: "archived",
} as const;

export type ClassLifecycleStatus =
	(typeof CLASS_LIFECYCLE_STATUS)[keyof typeof CLASS_LIFECYCLE_STATUS];

export type ClassMetaobject = {
	classId: string;
	instructorUserId?: string;
	registrationDeadlineAt?: string;
	lifecycleStatus: ClassLifecycleStatus;
	scheduleMetaobjectId?: string;
	campusBuildingMetaobjectId?: string;
};

export async function upsertClassMetaobject(
	classFields: ClassMetaobject
): Promise<string> {
	const variables: UpsertClassMetaobjectMutationVariables = {
		handle: {handle: classFields.classId, type: "class"},
		metaobject: {
			capabilities: {publishable: {status: "ACTIVE" as MetaobjectStatus}},
			// classFields is a "fat payload" (see ClassUpdated proto doc):
			// undefined means the field was cleared, not "unchanged." The
			// mutation metaobjectUpsert leaves omitted fields as-is, so we must
			// send every key explicitly in order to clear them.
			fields: [
				{key: "class_id", value: classFields.classId},
				{key: "instructor_user_id", value: classFields.instructorUserId ?? ""},
				{
					key: "registration_deadline_at",
					value: classFields.registrationDeadlineAt ?? "",
				},
				{key: "lifecycle_status", value: classFields.lifecycleStatus},
				{key: "schedule", value: classFields.scheduleMetaobjectId ?? ""},
				{
					key: "campus_building",
					value: classFields.campusBuildingMetaobjectId ?? "",
				},
			],
		},
	};
	const response = await makeShopifyRequest<UpsertClassMetaobjectMutation>(
		"admin",
		`#graphql
			mutation UpsertClassMetaobject(
				$handle: MetaobjectHandleInput!
				$metaobject: MetaobjectUpsertInput!
			) {
				metaobjectUpsert(handle: $handle, metaobject: $metaobject) {
					metaobject {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	if (!response.metaobjectUpsert) {
		throw new Error(
			"Shopify class metaobject upsert failed: metaobjectUpsert missing from Shopify response"
		);
	}

	const {userErrors, metaobject} = response.metaobjectUpsert;
	if (userErrors.length) {
		throw new Error(
			`Shopify class metaobject upsert failed: ${formatUserErrors(userErrors)}`
		);
	}
	if (!metaobject) {
		throw new Error(
			"Shopify class metaobject upsert failed: metaobject missing from Shopify response"
		);
	}

	return metaobject.id;
}

/**
 * Fetches the value of the specified metafield on the given Shopify product
 * variant, identified by its SKU. Returns the metafield value, or null if it's
 * not set (or the metafield doesn't exist). Throws if the variant doesn't exist.
 *
 * Intentionally inlines the SKU → variant lookup rather than calling
 * findProductVariantBySku and then fetching the metafield separately, so both
 * the ID lookup and the metafield read happen in a single Shopify round-trip.
 */
export async function getMetafieldFromProductVariant(
	sku: string,
	metafieldName: string
): Promise<string | null> {
	const variables: GetVariantMetafieldQueryVariables = {
		query: `sku:"${escapeSearchQueryValue(sku)}"`,
		namespace: METAFIELD_NAMESPACE,
		key: metafieldName,
	};
	const response = await makeShopifyRequest<GetVariantMetafieldQuery>(
		"admin",
		`#graphql
			query GetVariantMetafield(
				$query: String!
				$namespace: String!
				$key: String!
			) {
				productVariants(first: 1, query: $query) {
					nodes {
						id
						metafield(namespace: $namespace, key: $key) {
							value
						}
					}
				}
			}
		`,
		variables
	);

	const variant = response.productVariants.nodes[0];
	if (!variant) {
		throw new Error(
			`Shopify product variant metafield read failed: variant not found for SKU "${sku}"`
		);
	}

	return variant.metafield?.value ?? null;
}

// Shopify's codegen MetafieldInput type defines all of its fields as InputMaybe types,
// which makes them inherently nullable. However, for setMetafieldOnProductVariant we
// require that key, type, and value all be set. This type allows us to enforce that at
// the call site rather than relying on runtime null checks inside the function.
type RequiredNonNullPick<T, K extends keyof T> = {
	[P in K]-?: NonNullable<T[P]>;
};

/**
 * Sets the specified metafields on the given Shopify product variant (identified
 * by its SKU). Only the metafields provided in the input are updated; any other
 * metafields on the variant are left unchanged.
 */
export async function setMetafieldOnProductVariant(
	sku: string,
	metafield: RequiredNonNullPick<MetafieldInput, "key" | "type" | "value">
): Promise<void> {
	const variantId = await findProductVariantBySku(sku);
	if (!variantId) {
		throw new Error(
			`Shopify product variant metafield update failed: variant not found for SKU "${sku}"`
		);
	}

	const variables: MetafieldsSetMutationVariables = {
		metafields: [
			{
				ownerId: variantId,
				namespace: METAFIELD_NAMESPACE,
				key: metafield.key,
				type: metafield.type,
				value: metafield.value,
			},
		],
	};

	const response = await makeShopifyRequest<MetafieldsSetMutation>(
		"admin",
		`#graphql
			mutation MetafieldsSet($metafields: [MetafieldsSetInput!]!) {
				metafieldsSet(metafields: $metafields) {
					metafields {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on the result, and since we
	// don't want a response that doesn't have it to be treated like a success.
	if (!response.metafieldsSet) {
		throw new Error(
			"Shopify product variant metafield update failed: metafieldsSet missing from Shopify response"
		);
	}

	const {userErrors} = response.metafieldsSet;
	if (userErrors.length) {
		throw new Error(
			`Shopify product variant metafield update failed: ${formatUserErrors(userErrors)}`
		);
	}
}

/**
 * @param email Must not contain quotes (i.e. get straight from logged-in session, not just anywhere)
 */
export async function getCustomerWithCartMetafield(
	email: string
): Promise<ShopifyCustomerWithCartMetafield | null> {
	// Platform (aops-ba/platform-accounts, src/util/General.ts `isValidEmail`:
	// /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i, plus Type.String({format:
	// "email"}) on its endpoints) admits no `"` or `\`, so a validated email can
	// never carry the characters Shopify search treats specially. We interpolate it
	// raw, but fail loud if that invariant is ever violated rather than silently
	// escaping, which would just mask a caller passing an unvalidated email.
	//
	// TODO Make email a type that guards all Shopify requests that take emails.
	if (/["\\]/.test(email)) {
		log.error(
			{
				module: "packages/shopify/src/operations/admin.ts",
				fn: "getCustomerWithCartMetafield",
				label: "ValidateEmail",
				email,
			},
			"E_VALIDATING_EMAIL"
		);

		throw new Error(
			"getCustomerWithCartMetafield: email must not contain quotes or backslashes"
		);
	}

	const data =
		await makeShopifyRequest<GetCustomerByEmailWithCartMetafieldQuery>(
			"admin",
			`#graphql
			query GetCustomerByEmailWithCartMetafield($query: String!, $namespace: String!, $key: String!) {
				customers(first: 1, query: $query) {
					edges {
						node {
							id
							defaultEmailAddress {
								emailAddress
							}
							metafield(namespace: $namespace, key: $key) {
								id
								value
							}
						}
					}
				}
			}
		`,
			{
				query: `email:"${email}"`,
				namespace: METAFIELD_NAMESPACE,
				key: "cart_id",
			}
		);

	const node = data.customers.edges[0]?.node;
	if (!node) return null;

	return {
		customerShopifyId: node.id,
		email: node.defaultEmailAddress?.emailAddress ?? email,
		cartId: node.metafield?.value ?? null,
		cartMetafieldId: node.metafield?.id ?? null,
	};
}

export async function setCustomerCartMetafield(
	customerShopifyId: string,
	cartId: string
): Promise<string> {
	const data = await makeShopifyRequest<MetafieldsSetMutation>(
		"admin",
		`#graphql
			mutation MetafieldsSet($metafields: [MetafieldsSetInput!]!) {
				metafieldsSet(metafields: $metafields) {
					metafields {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		{
			metafields: [
				{
					ownerId: customerShopifyId,
					namespace: METAFIELD_NAMESPACE,
					key: "cart_id",
					value: cartId,
					type: "single_line_text_field",
				},
			],
		}
	);

	const errors = data.metafieldsSet?.userErrors ?? [];
	if (errors.length) {
		log.error(
			{
				module: "packages/shopify/src/operations/admin.ts",
				fn: "setCustomerCartMetafield",
				label: "SetCartMetafield",
				errors,
			},
			"E_SET_CART_METAFIELD"
		);

		throw new Error(
			`Failed to set cart metafield: ${errors.map((e) => e.message).join("; ")}`
		);
	}

	const metafieldId = data.metafieldsSet?.metafields?.[0]?.id;
	if (!metafieldId) {
		log.error(
			{
				module: "packages/shopify/src/operations/admin.ts",
				fn: "setCustomerCartMetafield",
				label: "NoCartMetafield",
				response: data.metafieldsSet,
			},
			"E_NO_CART_METAFIELD"
		);

		throw new Error("setCustomerCartMetafield: no metafield ID returned");
	}
	return metafieldId;
}

/**
 * TODO Do we want this in the the e-commerce projects initial release? Presuming
 * Internal Services Pod's OIDC implementation might do this for us. Possible that
 * might send an MSK event that our app needs to process, so keeping it for now.
 * @throws When there's already a customer with that email
 */
export async function createShopifyCustomer(input: {
	email: string;
	firstName?: string | null;
	lastName?: string | null;
}): Promise<ShopifyCustomer> {
	const data = await makeShopifyRequest<CustomerCreateMutation>(
		"admin",
		`#graphql
			mutation CustomerCreate($input: CustomerInput!) {
				customerCreate(input: $input) {
					customer {
						id
						defaultEmailAddress {
							emailAddress
						}
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		{
			input: {
				email: input.email,
				firstName: input.firstName ?? undefined,
				lastName: input.lastName ?? undefined,
			},
		}
	);

	if (!data.customerCreate) {
		throw new Error("Shopify returned no customerCreate payload");
	}
	const {customer, userErrors} = data.customerCreate;
	if (userErrors.length) {
		throw new Error(
			`Failed to create customer: ${userErrors.map((e) => e.message).join("; ")}`
		);
	}
	if (!customer) {
		throw new Error("Shopify returned no customer");
	}
	return {
		customerShopifyId: customer.id,
		email: customer.defaultEmailAddress?.emailAddress ?? input.email,
	};
}

/**
 * Does `customerDelete` instead of `customerRequestDataErasure` because the PoC
 * requires instant deletion rather than a CCPA/GDPR user submission.
 *
 * TODO Delete this before the initial release of e-commerce! This function is for
 * internal demos only and should **never** be used **ever**! Please use `customerRequestDataErasure`
 * to actually delete customers in compliance with international privacy law for
 * realsies and **not** this!
 *
 * @throws When the customer has an order
 * @deprecated
 */
export async function deleteShopifyCustomer(
	customerShopifyId: string
): Promise<void> {
	const data = await makeShopifyRequest<CustomerDeleteMutation>(
		"admin",
		`#graphql
			mutation CustomerDelete($input: CustomerDeleteInput!) {
				customerDelete(input: $input) {
					deletedCustomerId
					userErrors {
						field
						message
					}
				}
			}
		`,
		{input: {id: customerShopifyId}}
	);

	const errors = data.customerDelete?.userErrors ?? [];
	if (errors.length) {
		log.error(
			{
				module: "packages/shopify/src/operations/admin.ts",
				fn: "deleteShopifyCustomer",
				label: "DeleteCustomer",
				errors,
			},
			"E_DELETING_CUSTOMER"
		);

		throw new Error(
			`Failed to delete customer: ${errors.map((e) => e.message).join("; ")}`
		);
	}
}

export async function deleteCustomerCartMetafield(
	customerShopifyId: string
): Promise<void> {
	const data = await makeShopifyRequest<MetafieldsDeleteMutation>(
		"admin",
		`#graphql
			mutation MetafieldsDelete($metafields: [MetafieldIdentifierInput!]!) {
				metafieldsDelete(metafields: $metafields) {
					deletedMetafields {
						namespace
						key
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		{
			metafields: [
				{
					ownerId: customerShopifyId,
					namespace: METAFIELD_NAMESPACE,
					key: "cart_id",
				},
			],
		}
	);

	const errors = data.metafieldsDelete?.userErrors ?? [];
	if (errors.length) {
		log.error(
			{
				module: "packages/shopify/src/operations/admin.ts",
				fn: "deleteCustomerCartMetafield",
				label: "DeleteCartMetafield",
				errors,
			},
			"E_DELETING_CART_METAFIELD"
		);

		throw new Error(
			`Failed to delete cart metafield: ${errors.map((e) => e.message).join("; ")}`
		);
	}
}

/**
 * Fetch a Shopify customer by email address. Returns null if no customer with
 * that email exists. Throws if the Shopify request fails.
 */
export async function getCustomerByEmail(emailAddress: string) {
	const data = await makeShopifyRequest<GetCustomerByEmailQuery>(
		"admin",
		`#graphql
		query GetCustomerByEmail($query: String!) {
			customers(first: 1, query: $query) {
				pagedResults: edges {
					node {
						...CustomerFragment
					}
				}
			}
		}
		${CUSTOMER_FRAGMENT}`,
		{query: `email:"${emailAddress}"`}
	);

	const customerNode = data.customers.pagedResults[0]?.node;
	if (!customerNode) {
		return null;
	}

	// Safe to coerce type because Shopify guarantees email, name, and customer ID must be set.
	return toShopifyCustomer(customerNode as ToShopifyCustomerInput);
}

/**
 * Update an existing Shopify customer by their Shopify global ID. Only the
 * name fields the caller provides are sent; omitted ones are left as they are
 * in Shopify. Throws if the Shopify request fails or if Shopify returns
 * mutation-level userErrors.
 */
export async function updateShopifyCustomer(
	id: string,
	{firstName, lastName, platformUserId}: CustomerUpdateInput
) {
	const data = await makeShopifyRequest<CustomerUpdateMutation>(
		"admin",
		`#graphql
		mutation CustomerUpdate($input: CustomerInput!) {
			customerUpdate(input: $input) {
				customer {
					...CustomerFragment
				}
				userErrors {
					field
					message
				}
			}
		}
		${CUSTOMER_FRAGMENT}`,
		{
			input: {
				id,
				...(firstName !== undefined && {firstName}),
				...(lastName !== undefined && {lastName}),
				metafields: [
					{
						key: "platform_user_id",
						namespace: METAFIELD_NAMESPACE,
						type: "id",
						value: platformUserId,
					},
				],
			},
		}
	);

	const customer = data.customerUpdate?.customer;
	const userErrors = data.customerUpdate?.userErrors ?? [];
	if (userErrors.length > 0) {
		throw new Error(
			`Customer update failed: ${userErrors.map((e: (typeof userErrors)[0]) => e.message).join("; ")}`
		);
	}

	// Doing this to please TypeScript.
	if (!customer) {
		throw new Error("Shopify returned no customer on update");
	}

	return toShopifyCustomer(customer as ToShopifyCustomerInput);
}

export async function doesCustomerExistInShopify(
	platformUserId: string
): Promise<boolean> {
	const variables: DoesCustomerExistInShopifyQueryVariables = {
		identifier: {
			customId: {
				namespace: METAFIELD_NAMESPACE,
				key: "platform_user_id",
				value: platformUserId,
			},
		},
	};
	const response = await makeShopifyRequest<DoesCustomerExistInShopifyQuery>(
		"admin",
		`#graphql
			query DoesCustomerExistInShopify($identifier: CustomerIdentifierInput!) {
				customerByIdentifier(identifier: $identifier) {
					id
				}
			}
		`,
		variables
	);

	return !!response.customerByIdentifier;
}

/**
 * Creates/updates a Shopify customer, identified by their Platform user ID.
 * Note that fields not present in the input are left unchanged.
 */
export async function upsertCustomer(
	platformUserId: string,
	input: CustomerSetInput
): Promise<void> {
	const variables: UpsertCustomerMutationVariables = {
		identifier: {
			customId: {
				namespace: METAFIELD_NAMESPACE,
				key: "platform_user_id",
				value: platformUserId,
			},
		},
		input,
	};
	const response = await makeShopifyRequest<UpsertCustomerMutation>(
		"admin",
		`#graphql
			mutation UpsertCustomer(
				$identifier: CustomerSetIdentifiers
				$input: CustomerSetInput!
			) {
				customerSet(identifier: $identifier, input: $input) {
					customer {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on the result, and since we
	// don't want a response that doesn't have it to be treated like a success.
	const upsertResult = response.customerSet;
	if (!upsertResult) {
		throw new Error(
			"Shopify customer upsert failed: customerSet missing from response"
		);
	}

	const {userErrors} = upsertResult;
	if (userErrors.length) {
		throw new Error(
			`Shopify customer upsert failed: ${formatUserErrors(userErrors)}`
		);
	}
}

/**
 * Looks up the Shopify order line item for an enrollment by order ID, class ID
 * (variant SKU), and student platform user ID (`_Student ID` custom attribute).
 * The order ID may be a numeric legacy ID or an already-formed GID.
 *
 * Throws if the order is not found, if no matching line item exists, or if
 * more than one line item matches.
 */
export async function findOrderLineItemForEnrollment({
	orderId,
	classId,
	studentPlatformUserId,
}: FindOrderLineItemForEnrollmentInput): Promise<string> {
	const orderGid = toShopifyGid("Order", orderId);

	const response = await makeShopifyRequest<OrderLineItemsForEnrollmentQuery>(
		"admin",
		`#graphql
			query OrderLineItemsForEnrollment($orderId: ID!) {
				order(id: $orderId) {
					id
					lineItems(first: 50) {
						nodes {
							id
							sku
							customAttributes {
								key
								value
							}
							variant {
								sku
							}
						}
					}
				}
			}
		`,
		{orderId: orderGid}
	);

	if (!response.order) {
		throw new Error(
			`Shopify enrollment line item lookup failed: order "${orderGid}" not found`
		);
	}

	const lineItemId = findEnrollmentLineItemId(
		response.order.lineItems.nodes,
		classId,
		studentPlatformUserId
	);
	if (!lineItemId) {
		throw new Error(
			`Shopify enrollment line item lookup failed: no line item on order "${orderGid}" matches class "${classId}" and student "${studentPlatformUserId}"`
		);
	}

	return lineItemId;
}

/**
 * Marks an order's line item as fulfilled. Looks up the order's fulfillment
 * orders, fulfills only the fulfillment-order line items matching the given line
 * item, and leaves the rest of the order untouched. The order and line item IDs
 * may be numeric legacy IDs or already-formed GIDs.
 *
 * Idempotent: if the line item has already been fulfilled (no remaining quantity
 * on any open fulfillment order), this is a no-op. Throws if the order is not
 * found, the response is missing the fulfillmentCreate payload, or Shopify
 * returns mutation-level userErrors.
 */
export async function fulfillLineItem({
	orderId,
	lineItemId,
}: FulfillLineItemInput): Promise<void> {
	const orderGid = toShopifyGid("Order", orderId);
	const lineItemGid = toShopifyGid("LineItem", lineItemId);

	const response = await makeShopifyRequest<OrderFulfillmentOrdersQuery>(
		"admin",
		`#graphql
			query OrderFulfillmentOrders($orderId: ID!) {
				order(id: $orderId) {
					id
					fulfillmentOrders(first: 50) {
						nodes {
							id
							status
							lineItems(first: 50) {
								nodes {
									id
									remainingQuantity
									lineItem {
										id
									}
								}
							}
						}
					}
				}
			}
		`,
		{orderId: orderGid}
	);

	if (!response.order) {
		throw new Error(
			`Shopify item fulfillment failed: order "${orderGid}" not found`
		);
	}

	const lineItemsByFulfillmentOrder = selectFulfillableLineItems(
		response.order.fulfillmentOrders.nodes,
		lineItemGid
	);

	// Nothing left to fulfill for this line item — already fulfilled, so this
	// is an idempotent no-op.
	if (lineItemsByFulfillmentOrder.length === 0) return;

	const result = await makeShopifyRequest<CreateFulfillmentMutation>(
		"admin",
		`#graphql
			mutation CreateFulfillment($fulfillment: FulfillmentInput!) {
				fulfillmentCreate(fulfillment: $fulfillment) {
					fulfillment {
						id
						status
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		{fulfillment: {lineItemsByFulfillmentOrder, notifyCustomer: false}}
	);

	// Theoretically, this should never happen. It's just a defensive guard since
	// we're about to access the userErrors field on fulfillmentCreate, and since
	// we don't want a response that doesn't have it to be treated as a success.
	if (!result.fulfillmentCreate) {
		throw new Error(
			"Shopify item fulfillment failed: fulfillmentCreate missing from Shopify response"
		);
	}

	const {userErrors} = result.fulfillmentCreate;
	if (userErrors.length) {
		throw new Error(
			`Shopify item fulfillment failed: ${formatUserErrors(userErrors)}`
		);
	}
}

/**
 * Applies a delta to the available inventory for the product variant with the
 * given SKU. No-ops when the variant's inventory is untracked (unlimited seat
 * capacity) or when no variant matches the SKU (class isn't sold in Shopify).
 * Throws if Shopify returns errors.
 */
export async function adjustInventoryBySku(
	sku: string,
	delta: number,
	idempotencyKey: string
): Promise<void> {
	const lookupVariables: FindInventoryItemBySkuQueryVariables = {
		query: `sku:"${escapeSearchQueryValue(sku)}"`,
	};
	const lookup = await makeShopifyRequest<FindInventoryItemBySkuQuery>(
		"admin",
		`#graphql
			query FindInventoryItemBySku($query: String!) {
				productVariants(first: 1, query: $query) {
					nodes {
						id
						inventoryItem {
							id
							tracked
						}
					}
				}
			}
		`,
		lookupVariables
	);

	const variant = lookup.productVariants.nodes[0];
	if (!variant) {
		log.info(
			{sku},
			"Skipping inventory adjustment: no product variant found for SKU"
		);
		return;
	}
	if (!variant.inventoryItem) {
		throw new Error(
			`Shopify inventory adjustment failed: variant for SKU "${sku}" has no inventoryItem`
		);
	}
	// Untracked variants are unlimited-capacity classes; there is no seat
	// count to restore when an enrollment is dropped.
	if (!variant.inventoryItem.tracked) {
		log.info(
			{sku, variantId: variant.id},
			"Skipping inventory adjustment: variant inventory is not tracked"
		);
		return;
	}

	const variables: AdjustInventoryBySkuMutationVariables = {
		idempotencyKey,
		input: {
			name: "available",
			reason: "restock",
			changes: [
				{
					inventoryItemId: variant.inventoryItem.id,
					locationId: DEFAULT_LOCATION_ID,
					delta,
					changeFromQuantity: null,
				},
			],
		},
	};
	const response = await makeShopifyRequest<AdjustInventoryBySkuMutation>(
		"admin",
		`#graphql
			mutation AdjustInventoryBySku(
				$input: InventoryAdjustQuantitiesInput!
				$idempotencyKey: String!
			) {
				inventoryAdjustQuantities(input: $input)
					@idempotent(key: $idempotencyKey) {
					inventoryAdjustmentGroup {
						id
					}
					userErrors {
						field
						message
					}
				}
			}
		`,
		variables
	);

	if (!response.inventoryAdjustQuantities) {
		throw new Error(
			"Shopify inventory adjustment failed: inventoryAdjustQuantities missing from Shopify response"
		);
	}

	const {userErrors, inventoryAdjustmentGroup} =
		response.inventoryAdjustQuantities;
	if (userErrors.length) {
		throw new Error(
			`Shopify inventory adjustment failed: ${formatUserErrors(userErrors)}`
		);
	}
	if (!inventoryAdjustmentGroup) {
		throw new Error(
			"Shopify inventory adjustment failed: Shopify returned no inventoryAdjustmentGroup"
		);
	}
}

/**
 * Get a course offering (product) with its schedule (variants)
 * by course offering id and an optional campus id.
 */
export async function getCourseOfferingScheduleById(
	courseOfferingId: string,
	campusId: string | null
): Promise<CourseOfferingProduct | null> {
	const productId = await findProductIdByCourseOfferingIdAndCampusId(
		courseOfferingId,
		campusId
	);
	if (!productId) {
		return null;
	}

	return getCourseOfferingWithSchedule(productId);
}
